/**
 * Ryan O'Dowd
 * 2022-03-31
 * © Copyright 2022 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import React, {
  useCallback,
  useMemo,
  useState,
} from 'react';
import {
  TouchableOpacity,
  View,
} from 'react-native';
import Autocomplete from 'react-native-autocomplete-input';
import CPassage from '@classes/Passage';
import GameTemplate from '@containers/Practice/GameTemplate';
import Text from '@components/common/Text';
import getStyles from './styles';
import hooks from '@hooks';
import utilities from '@utils/utilities';

const References = (props: ReferencesProps) => {
  const styles = hooks.useStyles(getStyles);

  const [_selectedReference, setSelectedReference] = useState(null);
  const [_questionComplete, setQuestionComplete] = useState(false); // @TODO: abstract this?
  const [_isCorrect, setIsCorrect] = useState(false);
  const [_isIncorrect, setIsIncorrect] = useState(false);
  const [_showCorrectAnswer, setShowCorrectAnswer] = useState(false);
  const [_bookNameText, setBookNameText] = useState('');
  const [_chapterText, setChapterText] = useState('');
  const [_verseText, setVerseText] = useState('');

  const activeGoalPassages = hooks.useTypedSelector((state) => state.goals[state.settingsActiveGoalId].modules.flatMap((m) => m.assignments.map((a) => a.assignment_object.passage)).filter((p) => p), (oldValue, newValue) => JSON.stringify(oldValue) === JSON.stringify(newValue)); // @TODO: got issue in crashlytics for no active goal, but i don't want to null check because that will cause an infinite loop when we pick references from an empty list of passages...
  const numVersesInGoal = activeGoalPassages.reduce((curr, p) => curr + (p.end_verse_number - p.start_verse_number) + 1, 0);

  const allBookNames = hooks.useTypedSelector((state) => Object.values(state.bookNames[props.passage.translation_id]), (oldValue, newValue) => JSON.stringify(oldValue) === JSON.stringify(newValue));

  const verseNum = useMemo(() => {
    return props.passage.start_verse_number + Math.floor(Math.random() * (props.passage.end_verse_number - props.passage.start_verse_number + 1));
  }, [props.passage]);

  const singleVersePassage = {
    ...props.passage,
    start_verse_number: verseNum,
    end_verse_number: verseNum,
  };
  const verse = hooks.useGetVersesForPassages([singleVersePassage])[props.passage.id][0];

  const _pickRandomReferenceInGoal = useCallback((excludeReferences) => {
    let reference = null;
    while (!reference || excludeReferences.includes(reference)) {
      const tempPassage = activeGoalPassages[Math.floor(Math.random() * activeGoalPassages.length)];
      const thisChapterNum = tempPassage.chapter_number;
      const thisVerseNumber = tempPassage.start_verse_number + Math.floor(Math.random() * (tempPassage.end_verse_number - tempPassage.start_verse_number + 1)); // @TODO: can be problematic for verse ranges
      reference = CPassage.createFromObject({
        ...tempPassage,
        chapterNumber: thisChapterNum,
        chapter_number: thisChapterNum,
        startVerseNumber: thisVerseNumber,
        endVerseNumber: thisVerseNumber,
        start_verse_number: thisVerseNumber, // @TODO: shouldn't need this...remove from CPassage properties
        end_verse_number: thisVerseNumber, // @TODO: shouldn't need this...remove from CPassage properties
      }).prettyPassageName();

      if (excludeReferences.length >= numVersesInGoal) {
        // there are not enough verses in this passage
        const thisChapter = CPassage.getChapterByBookIdAndNumber(tempPassage.book_id, tempPassage.chapter_number);
        const numVersesInThisChapter = thisChapter.number_of_verses;

        let chapterNumber = thisChapterNum;
        let numVersesToChooseFrom = numVersesInThisChapter;
        if (numVersesToChooseFrom <= excludeReferences.length) {
          const numChaptersInThisBook = CPassage.getBookFromChapterId(thisChapter.id).number_of_chapters;
          chapterNumber = Math.ceil(Math.random() * numChaptersInThisBook);
          const randomChapter = CPassage.getChapterByBookIdAndNumber(tempPassage.book_id, chapterNumber);
          numVersesToChooseFrom = randomChapter.number_of_verses;
        }
        const randomVerseNumber = Math.ceil(Math.random() * numVersesToChooseFrom); // @TODO: can be problematic for verse ranges
        reference = CPassage.createFromObject({
          ...tempPassage,
          chapterNumber,
          chapter_number: chapterNumber, // @TODO: shouldn't need this...remove from CPassage properties
          startVerseNumber: randomVerseNumber,
          endVerseNumber: randomVerseNumber,
          start_verse_number: randomVerseNumber, // @TODO: shouldn't need this...remove from CPassage properties
          end_verse_number: randomVerseNumber, // @TODO: shouldn't need this...remove from CPassage properties
        }).prettyPassageName();
      }
    }

    return reference;
  }, [activeGoalPassages, numVersesInGoal]);

  // https://stackoverflow.com/questions/2450954/how-to-randomize-shuffle-a-javascript-array
  const _shuffleArray = (array) => {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      const temp = array[i];
      array[i] = array[j];
      array[j] = temp;
    }
  };

  const correctReference = CPassage.createFromObject(singleVersePassage).prettyPassageName();
  const referenceOptions = useMemo(() => {
    const options = [correctReference]; // @TODO: prevent random dupes (list -> set -> list) // @TODO: scramble the list
    const numFalseOptions = props.difficulty === 'easy' ? 3 : 5;
    [...Array(numFalseOptions)].forEach(() => {
      options.push(_pickRandomReferenceInGoal(options));
    });

    _shuffleArray(options);
    return options;
  }, [correctReference, _pickRandomReferenceInGoal, props.difficulty]);

  const _isValidBook = (bn) => {
    if (!bn) {
      return false;
    }

    bn = bn.toLocaleLowerCase();
    return allBookNames.some((b: any) => b.name.toLocaleLowerCase() === bn);
  };

  return (
    <GameTemplate
      subHeaderHeight={props.subHeaderHeight}
      questionComplete={_questionComplete}
      isIncorrect={_isIncorrect}
      isCorrect={_isCorrect}
      isCheckAnswerButtonDisabled={props.difficulty === 'hard' ? !(_isValidBook(_bookNameText) && _chapterText && _verseText) : !_selectedReference}
      onPass={() => props.onPass(_isCorrect, 1)}// @TODO: could be more than one in medium and hard modes
      onCheckAnswer={() => {
        props.onCheckAnswer();
        // @TODO: if failed, add to practice
        // @TODO: add score
        setQuestionComplete(true);
        // @TODO: set state for correct or not and display value to user
        if (props.difficulty === 'hard' ? correctReference.toLocaleLowerCase() === `${_bookNameText} ${_chapterText}:${_verseText}`.toLocaleLowerCase() : _selectedReference === correctReference) {
          // @TODO: play sound
          setIsCorrect(true);
        } else {
          // @TODO: play sound
          setIsIncorrect(true);
        }
        setShowCorrectAnswer(true);
      }}
    >
      {props.difficulty === 'hard' && _showCorrectAnswer && (
        <Text style={[styles.chapterAndVerse, styles.correctReference]}>{correctReference}</Text>
      )}
      <Text style={styles.verseText}>{utilities.removeVerseFormatting(verse.verseText)}</Text>
      <View style={styles.optionsWrapper}>
        {props.difficulty === 'hard'
          ? (
            <View style={styles.customInputWrapper}>
              <View style={styles.autocompleteWrapper}>
                <View style={styles.autocompleteContainer}>
                  <Autocomplete
                    editable={!_showCorrectAnswer}
                    containerStyle={styles.bookContainer}
                    inputContainerStyle={[styles.autocompleteContainerInner, _showCorrectAnswer && CPassage.getBookName(props.passage.translation_id, props.passage.book_id) !== _bookNameText && styles.incorrect]}
                    listContainerStyle={styles.autocompleteListContainerStyle}
                    style={[styles.customTextInput, _showCorrectAnswer && CPassage.getBookName(props.passage.translation_id, props.passage.book_id) !== _bookNameText && styles.incorrect]}
                    hideResults={!_bookNameText || _bookNameText.length < 2 || _isValidBook(_bookNameText)}
                    data={allBookNames.filter((b: any) => b.name.toLocaleLowerCase().includes(_bookNameText ? _bookNameText.toLocaleLowerCase() : ''))}
                    value={_bookNameText}// @TODO: this should be alert color if _showCorrectAnswer and incorrect or accent if _showCorrectAnswer and correct
                    onBlur={() => setBookNameText(_bookNameText?.trim() || '')}
                    onChangeText={(bookName) => setBookNameText(bookName)}
                    placeholder='Book'
                    flatListProps={{// @TODO: options are not scrollable
                      keyExtractor: (b: any) => b.id,
                      renderItem: ({item}: any) => {
                        return (
                          <TouchableOpacity
                            key={item.id}
                            onPress={() => setBookNameText(item.name)}
                          >
                            <Text style={[styles.customTextInput, styles.autocompleteOptionText]}>{item.name}</Text>
                          </TouchableOpacity>
                        );
                      },
                      keyboardShouldPersistTaps: 'always',
                    }}
                  />
                  <Autocomplete
                    editable={!_showCorrectAnswer}
                    containerStyle={styles.numberInputContainer}
                    inputContainerStyle={[styles.autocompleteContainerInner, styles.chapterVerseContainer, _showCorrectAnswer && +props.passage.chapter_number !== +_chapterText && styles.incorrect]}
                    style={[styles.customTextInput, _showCorrectAnswer && +props.passage.chapter_number !== +_chapterText && styles.incorrect]}
                    hideResults={true}
                    data={[]}
                    value={_chapterText}// @TODO: this should be alert color if _showCorrectAnswer and incorrect or accent if _showCorrectAnswer and correct
                    onChangeText={(chapterNum) => setChapterText(chapterNum.replace(/[^0-9]/g, ''))}
                    keyboardType='numeric'
                    maxLength={3}
                    placeholder='#'
                  />
                  <Text style={styles.metaText}>:</Text>
                  <Autocomplete
                    editable={!_showCorrectAnswer}
                    containerStyle={styles.numberInputContainer}
                    inputContainerStyle={[styles.autocompleteContainerInner, styles.chapterVerseContainer, _showCorrectAnswer && +verseNum !== +_verseText && styles.incorrect]}
                    style={[styles.customTextInput, _showCorrectAnswer && +verseNum !== +_verseText && styles.incorrect]}
                    hideResults={true}
                    data={[]}
                    value={_verseText}// @TODO: this should be alert color if _showCorrectAnswer and incorrect or accent if _showCorrectAnswer and correct
                    onChangeText={(vn) => setVerseText(vn.replace(/[^0-9]/g, ''))}
                    keyboardType='numeric'
                    maxLength={3}
                    placeholder='#'
                  />
                </View>
              </View>
            </View>
          ) : (
            referenceOptions.map((option) => {
              const optionStyles = [styles.option];
              const optionTextStyles = [styles.optionText];
              if (option === _selectedReference) {
                optionStyles.push(styles.selectedOption);
                optionTextStyles.push(styles.selectedOptionText);
                if (_showCorrectAnswer) {
                  optionStyles.push(styles.incorrectOption);
                  optionTextStyles.push(styles.incorrectOptionText);
                }
              }
              if (_showCorrectAnswer && option === correctReference) {
                optionStyles.push(styles.correctOption);
                optionTextStyles.push(styles.correctOptionText);
              }

              return (
                // @TODO: if _showCorrectAnswer, change styles to make guess alert and correct answer accent
                <TouchableOpacity
                  key={option}
                  style={optionStyles}
                  disabled={_questionComplete}
                  onPress={() => setSelectedReference(option)}
                >
                  <Text style={optionTextStyles}>{option}</Text>
                </TouchableOpacity>
              );
            })
          )
        }
      </View>
    </GameTemplate>
  );
};

class ReferencesProps {
  difficulty: 'easy' | 'medium' | 'hard';
  onCheckAnswer: () => void;
  onPass: (isCorrect: boolean, numVerses: number) => void;
  passage: any;
  subHeaderHeight: number;
};

export default References;
